import { BSON } from 'realm-web';

import { STOCK_CHANGES_COLLECTION } from '@/constants/constants';
import { getCollection } from '@/utils';
import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { Outlet, User, Variant } from '@/types';

export interface ProductHistory {
  _id: string;
  productId: number;
  baseAction: string;
  specificAction: string;
  companyId: string;
  outlet: Outlet;
  outletId: string;
  quantity: number;
  variant: Variant;
  variantId: string;
  user: User;
  updatedQuantity: string;
  createdAt: string;
  name?: string;
}

export const productHistoryApiSlice = createApi({
  reducerPath: 'productHistoryApi',
  baseQuery: fakeBaseQuery(),
  endpoints: (builder) => ({
    getProductHistory: builder.query<ProductHistory[], Record<string, any>>({
      async queryFn({ productId, filters }) {
        try {
          const productCollection = getCollection(STOCK_CHANGES_COLLECTION);

          const res = await productCollection?.aggregate([
            {
              $match: {
                productId: new BSON.ObjectID(productId),
                ...filters,
                ...(filters.createdAt && {
                  createdAt: {
                    $gte: new Date(filters.createdAt.$gte),
                    $lte: new Date(filters.createdAt.$lte),
                  },
                }),
              },
            },
            {
              $lookup: {
                from: 'variants',
                localField: 'variantId',
                foreignField: '_id',
                as: 'variant',
              },
            },
            {
              $lookup: {
                from: 'users',
                localField: 'userId',
                foreignField: '_id',
                as: 'user',
              },
            },
            {
              $lookup: {
                from: 'outlets',
                localField: 'outletId',
                foreignField: '_id',
                as: 'outlet',
              },
            },
            {
              $addFields: {
                user: { $arrayElemAt: ['$user', 0] },
                outlet: { $arrayElemAt: ['$outlet', 0] },
                variant: { $arrayElemAt: ['$variant', 0] },
              },
            },
            {
              $sort: { createdAt: -1 },
            },
          ]);
          if (!res) {
            return { data: [] };
          }
          return { data: BSON.EJSON.deserialize(res) as ProductHistory[] };
        } catch (error: any) {
          return { error: error.message || 'Unknown error' };
        }
      },
    }),
  }),
});

export const { useGetProductHistoryQuery } = productHistoryApiSlice;
